import { useEffect, useRef, useState } from 'react';
import { fetchFirmwareJob, type FirmwareJob } from '../services/firmwareService';
import { eventBus } from '../services/EventBus';
import { getToken } from '../services/auth';

function isActiveStatus(status: string | undefined): boolean {
  return status === 'queued' || status === 'running';
}

function isUnauthorized(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401;
}

export function useFirmwareJobMonitor(enabled: boolean) {
  const [job, setJob] = useState<FirmwareJob | null>(null);
  const [justFinished, setJustFinished] = useState<FirmwareJob | null>(null);
  const prevRef = useRef<{ id: number; status: string } | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const unsub = eventBus.on('firmware:job', (next: FirmwareJob) => {
      if (!next) return;
      setJob(next);
      prevRef.current = { id: next.id, status: next.status };
    });
    return unsub;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer = 0;

    const stop = () => {
      cancelled = true;
      window.clearInterval(timer);
    };

    const tick = async () => {
      if (cancelled) return;
      if (!getToken()) {
        stop();
        return;
      }
      try {
        const next = await fetchFirmwareJob();
        if (cancelled) return;
        setJob(next);
        const prev = prevRef.current;
        if (
          next
          && prev
          && prev.id === next.id
          && isActiveStatus(prev.status)
          && next.status === 'done'
        ) {
          setJustFinished(next);
        }
        if (next) {
          prevRef.current = { id: next.id, status: next.status };
        }
      } catch (error) {
        if (isUnauthorized(error) || !getToken()) {
          stop();
        }
        /* el banner queda oculto si falla */
      }
    };

    const intervalMs = isActiveStatus(job?.status) ? 2000 : 5000;
    timer = window.setInterval(tick, intervalMs);
    void tick();
    const unsubExpired = eventBus.on('token-expired', stop);
    return () => {
      stop();
      unsubExpired();
    };
  }, [enabled, job?.status]);

  return {
    justFinished,
    clearJustFinished: () => setJustFinished(null),
  };
}
